import React, { useEffect, useState } from "react";
import CaseService from "../../../services/CaseService";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import { Autocomplete } from "@material-ui/lab";
import { makeStyles, Popper, Typography } from "@material-ui/core";
import styled from "styled-components";
import theme from "../../../assets/theme";
import { CustomInputField } from "../../common/FormInputs";

const useStyles = makeStyles(() => ({
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "15px",
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    maxHeight: "320px",
    overflowY: "auto",
  },
  option: {
    padding: "10px 16px",
    "&:hover": {
      backgroundColor: "#f8f9ff",
    },
  },
  groupLabel: {
    backgroundColor: "#f0fdf4",
    color: "#0f766e",
    fontWeight: 700,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "10px 16px",
    borderBottom: "1px solid #e0e0e0",
  },
  noOptions: {
    padding: "16px",
    color: "#8294a5",
    fontSize: "14px",
  },
}));

const CustomPopper = (props) => (
  <Popper {...props} placement="bottom-start" style={{ width: "260px" }} />
);

const DocChangeType = ({ rowData, documentTypes }) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const docId = rowData?.id;
  const caseId = rowData?.entityId;

  const [selectedValue, setSelectedValue] = useState(null);

  const fetchInitialValue = () => ({
    category: rowData?.url?.category || "",
    name: rowData?.url?.uploadType || "Other",
  });

  useEffect(() => {
    setSelectedValue(fetchInitialValue()); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData?.url]);

  const changeDocType = async (category, value) => {
    if (!value) return;
    try {
      const payload = { uploadType: value, category };
      const res = await CaseService.changeDocType(payload, caseId, docId);
      if (res) {
        enqueueSnackbar("Document type updated successfully", {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message || "Failed to update document type", {
        variant: "error",
      });
    }
  };

  const documentTypeOptions = Object.entries(documentTypes || {}).flatMap(
    ([category, names]) =>
      Array.isArray(names)
        ? names.map((name) => ({
            category,
            name,
          }))
        : [],
  );

  return (
    <Container>
      <Autocomplete
        id="doc-type-select"
        size="small"
        value={selectedValue}
        onChange={(event, newValue) => {
          setSelectedValue(newValue);
          if (newValue) changeDocType(newValue.category, newValue.name);
        }}
        options={documentTypeOptions}
        groupBy={(option) => option.category}
        getOptionLabel={(option) => option.name || ""}
        renderInput={(params) => (
          <CustomInputField
            {...params}
            variant="outlined"
            placeholder="Select type"
            style={{ width: "100%" }}
          />
        )}
        renderOption={(option) => (
          <Typography className={classes.option}>{option.name}</Typography>
        )}
        renderGroup={(params) => (
          <div key={params.group}>
            <div className={classes.groupLabel}>{params.group}</div>
            {params.children}
          </div>
        )}
        PopperComponent={CustomPopper}
        classes={{
          listbox: classes.listbox,
          noOptions: classes.noOptions,
        }}
        disableClearable
        noOptionsText="No matching document type"
      />
    </Container>
  );
};

export default DocChangeType;

const Container = styled.div`
  width: 100%;
  min-width: 160px;

  .MuiAutocomplete-inputRoot {
    padding: 2px 8px !important;
    background-color: #f8f9ff;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    height: 32px;
    min-height: 32px;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputBase-input {
    font-size: 13.5px;
    font-weight: 500;
    color: #1f2937;
    padding: 4px 6px !important;
    line-height: 1.4;
  }

  .Mui-focused .MuiAutocomplete-inputRoot {
    border-color: #e1e3ee;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  @media ${theme?.breakpoints?.sm_up} {
    min-width: 175px;
  }
`;
